// Banner install — status enums, chip/display maps, snippet generation.

/** Framework tabs for the Install step install snippet. */
export const INSTALL_SNIPPET_FRAMEWORKS = [
  { key: 'html', label: 'HTML' },
  { key: 'react', label: 'React' },
  { key: 'nextjs', label: 'Next.js' },
  { key: 'nuxt', label: 'Nuxt' },
  { key: 'vue', label: 'Vue' },
]

/**
 * Plain <script> tag for static HTML shells (no async/defer - load early in <head>).
 * @param {string} scriptUrl
 * @returns {string}
 */
function buildHeadScriptTag(scriptUrl) {
  return ['<script', '    id="cookiearmor-bootstrap"', `    src="${scriptUrl}"`, '></script>'].join(
    '\n'
  )
}

/**
 * Generates the framework-specific install snippet for a created banner.
 * HTML, React, and Vue place a blocking <script> early in the document <head>.
 * Next.js uses <Script strategy="beforeInteractive">; Nuxt uses useHead in app.vue.
 *
 * @param {string} scriptUrl - Full CDN script URL
 * @param {string} frameworkKey - One of INSTALL_SNIPPET_FRAMEWORKS[].key
 * @returns {string}
 */
export function generateInstallSnippet(scriptUrl, frameworkKey) {
  const headScriptTag = buildHeadScriptTag(scriptUrl)

  switch (frameworkKey) {
    case 'html':
      return ['<!-- Add to <head>, before other third-party scripts -->', headScriptTag].join('\n')

    case 'react':
      return ['<!-- public/index.html - first script in <head> -->', headScriptTag].join('\n')

    case 'nextjs':
      return [
        "import Script from 'next/script'",
        '',
        '<Script',
        '    id="cookiearmor-bootstrap"',
        `    src="${scriptUrl}"`,
        '    strategy="beforeInteractive"',
        '/>',
      ].join('\n')

    case 'nuxt':
      return [
        '// app.vue - add inside <script setup>, before other scripts',
        'useHead({',
        '  script: [',
        '    {',
        '      id: "cookiearmor-bootstrap",',
        `      src: "${scriptUrl}",`,
        '    },',
        '  ],',
        '})',
      ].join('\n')

    case 'vue':
      return ['<!-- index.html - first script in <head> -->', headScriptTag].join('\n')

    default:
      return ''
  }
}

export const BANNER_INSTALL_STATUS = {
  INSTALLED: 'installed',
  MISSING: 'missing',
  ERROR: 'error',
  /** Checker reached the site but was refused (e.g. WAF / 403). */
  BLOCKED: 'blocked',
}

/**
 * Install status pill: `$ax-colors` utilities on `q-chip` + matching status dot.
 * @type {Record<string, { label: string, chipClass: string, dotClass: string }>}
 */
export const BANNER_INSTALL_STATUS_CHIP_MAP = {
  [BANNER_INSTALL_STATUS.INSTALLED]: {
    label: 'Script Installed',
    chipClass: 'bg-positive-light text-positive',
    dotClass: 'bg-positive',
  },
  [BANNER_INSTALL_STATUS.MISSING]: {
    label: 'Script Missing',
    chipClass: 'bg-negative-light text-negative',
    dotClass: 'bg-negative',
  },
  [BANNER_INSTALL_STATUS.ERROR]: {
    label: "Couldn't check",
    chipClass: 'bg-border-light text-info',
    dotClass: 'bg-info',
  },
  [BANNER_INSTALL_STATUS.BLOCKED]: {
    label: 'Access blocked',
    chipClass: 'bg-cta-light text-cta-dark',
    dotClass: 'bg-cta-dark',
  },
}

/**
 * Overview install status card hero: icon, label, description, and token classes.
 * `label` must match BANNER_INSTALL_STATUS_CHIP_MAP for the same state.
 * @type {Record<string, { icon: string, label: string, description: string|null, iconWrapClass: string, iconClass: string }>}
 */
export const BANNER_INSTALL_STATUS_DISPLAY_MAP = {
  [BANNER_INSTALL_STATUS.INSTALLED]: {
    icon: 'shield-check',
    label: 'Script Installed',
    description: 'Script detected',
    iconWrapClass: 'bg-positive-light',
    iconClass: 'text-positive',
  },
  [BANNER_INSTALL_STATUS.MISSING]: {
    icon: 'shield-alert',
    label: 'Script Missing',
    description: 'Waiting for script detection',
    iconWrapClass: 'bg-negative-light',
    iconClass: 'text-negative',
  },
  [BANNER_INSTALL_STATUS.ERROR]: {
    icon: 'alert-circle',
    label: "Couldn't check",
    description: null,
    iconWrapClass: 'bg-border-light',
    iconClass: 'text-info',
  },
  [BANNER_INSTALL_STATUS.BLOCKED]: {
    icon: 'shield-off',
    label: 'Access blocked',
    description: 'Your site blocked our install checker',
    iconWrapClass: 'bg-cta-light',
    iconClass: 'text-cta-dark',
  },
}

/**
 * Install status failure reasons: maps BE `reason` codes to user-facing copy.
 * Used for `error` and `blocked` states. Unmapped reasons fall back to the raw
 * BE string in the resolver.
 * @type {Record<string, string>}
 */
export const BANNER_INSTALL_STATUS_REASON_MESSAGES = {
  http_403: 'Access denied. Your site blocked our install checker.',
  http_404: 'Site not found.',
}
